import { Dispatch } from "redux";
import ThunkAction from "../../interfaces/ThunkAction";
import Cart from "../../interfaces/cart/Cart";
import { setCart } from "./cartActions";
import CartProductClient from "../../clients/cart/CartProductClient";
import CartProductConfig, { CartProductConfigs } from "../../interfaces/cart/CartProductConfig";
import Action from "../../interfaces/Action";
import ActionTypeEnum from "../../enums/ActionTypeEnum";
import { CartUpsellConfig } from "../../interfaces/cart/CartUpsell";
import eventEmitter from "../../events/eventEmitter";
import CartProductsAddedEvent from "../../events/events/CartProductsAddedEvent";
import CartProductRemovedEvent from "../../events/events/CartProductRemovedEvent";
import CartProductUtil from "../../utils/CartProductUtil";
import GlobalState from "../../interfaces/GlobalState";
import { preloadUpsellImages } from "../../utils/preloadImages";

// ------------------------- [ Action Creators ] -------------------------

const setUpsells = (upsells: CartUpsellConfig): Action => ({
    type: ActionTypeEnum.UPSELLS_SET,
    upsells,
});

export const clearUpsells = (): Action => setUpsells({ layout: null, upsells: [] });

// ------------------------- [ Thunks ] -------------------------

const cartProductClient = new CartProductClient();

export const addProducts = (
    productConfigs: CartProductConfigs,
    cartUuid?: string,
    listName?: string,
): ThunkAction<Promise<Cart>> => async (dispatch: Dispatch) => {
    const response = await cartProductClient.addProducts(productConfigs, window.location.pathname, cartUuid);
    const cart = response.data;
    const cartProductsAdded = CartProductUtil.getProducts(cart, productConfigs);

    dispatch(setCart(cart));
    eventEmitter.emit(
        new CartProductsAddedEvent(cartProductsAdded, cart, listName),
    );

    return cart;
};

export const addProduct = (productConfig: CartProductConfig, cartUuid?: string, listName?: string): ThunkAction<Promise<Cart>> => (
    addProducts([productConfig], cartUuid, listName)
);

export const updateProduct = (
    productConfig: CartProductConfig,
    cartUuid: string,
): ThunkAction<Promise<Cart>> => async (dispatch: Dispatch, getState: () => GlobalState) => {
    // We need to grab the product from the cart in state BEFORE we update
    // because the product won't exist in the cart AFTER the update if the product was removed.
    const cartBeforeUpdate = getState().cart;
    let cartProductUpdated = null;

    if (cartBeforeUpdate) {
        cartProductUpdated = CartProductUtil.getProduct(cartBeforeUpdate, productConfig);
    }

    // Update the cart product.
    const response = await cartProductClient.updateProduct(productConfig, cartUuid);
    const cart = response.data;

    dispatch(setCart(cart));

    // We removed the product. Emit the event.
    if (productConfig.quantity === 0) {
        if (cartProductUpdated) {
            eventEmitter.emit(new CartProductRemovedEvent(cartProductUpdated, cart));
        } else {
            console.warn(
                "Event: Cannot emit `CartProductRemovedEvent` because the removed product couldn't be determined.",
            );
        }
    }

    return cart;
};

export const loadUpsells = (cartUuid: string): ThunkAction<Promise<CartUpsellConfig>> => async (
    dispatch: Dispatch,
) => {
    const response = await cartProductClient.getUpsells(cartUuid);
    preloadUpsellImages(response.data);
    dispatch(setUpsells(response.data));
    return response.data;
};
